import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Bell, LogOut, FileText, LayoutDashboard, ChevronDown, MessageCircle, Phone } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatDate } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifs, setNotifs] = useState([]);

  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data || []);
    } catch (_) {}
  };

  useEffect(() => {
    if (user && user.id) {
      loadNotifs();
      const t = setInterval(loadNotifs, 15000);
      return () => clearInterval(t);
    }
  }, [user?.id, location.pathname]);

  const unread = notifs.filter((n) => !n.read).length;

  const markAllRead = async () => {
    try { await api.post("/notifications/read-all"); loadNotifs(); } catch (_) {}
  };

  if (!user || user === false) return children;
  const isAdmin = user.role === "admin";

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-30 glass" style={{ borderBottom: "1px solid rgba(226,232,240,0.6)" }}>
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-3.5 flex items-center justify-between gap-6">
          <Link to={isAdmin ? "/admin" : "/dashboard"} data-testid="shell-home-link" className="flex items-center gap-3">
            <img src={BRAND.logoUrl} alt="" className="h-10 w-10 object-contain" />
            <div className="leading-tight">
              <div className="font-heading font-bold text-[15px] tracking-tight text-navy">
                ACCURATE CLAIMS
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 -mt-0.5">
                {isAdmin ? "Admin Console" : "Client Portal"}
              </div>
            </div>
          </Link>

          <nav className="flex items-center gap-1.5">
            {isAdmin ? (
              <NavLink to="/admin" testId="nav-admin-inbox" icon={<LayoutDashboard className="w-4 h-4" />}>Inbox</NavLink>
            ) : (
              <>
                <NavLink to="/dashboard" testId="nav-client-dashboard" icon={<LayoutDashboard className="w-4 h-4" />}>Cases</NavLink>
                <NavLink to="/dashboard/new" testId="nav-client-new" icon={<FileText className="w-4 h-4" />}>New Case</NavLink>
              </>
            )}

            <a href={BRAND.whatsappUrl} target="_blank" rel="noreferrer" data-testid="shell-whatsapp" className="hidden md:inline-flex h-9 px-3 items-center gap-1.5 rounded-full text-xs font-semibold text-emerald-brand border border-emerald-brand/30 hover:bg-emerald-brand hover:text-white transition-colors">
              <MessageCircle className="w-4 h-4" /> Help
            </a>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="shell-notifications-trigger"
                  className="relative h-9 w-9 inline-flex items-center justify-center rounded-full border border-slate-200 bg-white hover:border-navy transition-colors ml-1"
                  aria-label="Notifications"
                >
                  <Bell className="w-4 h-4" />
                  {unread > 0 && (
                    <span
                      data-testid="notifications-unread-badge"
                      className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-emerald-brand text-white text-[10px] font-bold"
                    >
                      {unread}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-96 rounded-2xl border border-slate-200 bg-white p-0 shadow-xl">
                <DropdownMenuLabel className="flex items-center justify-between py-3 px-4 border-b border-slate-100">
                  <span className="font-heading font-semibold text-sm">Notifications</span>
                  <button
                    data-testid="notifications-mark-all-read"
                    onClick={markAllRead}
                    className="text-xs text-emerald-brand hover:underline"
                  >
                    Mark all read
                  </button>
                </DropdownMenuLabel>
                <div className="max-h-96 overflow-y-auto">
                  {notifs.length === 0 && (
                    <div className="py-10 text-center text-xs text-slate-400">
                      No notifications yet
                    </div>
                  )}
                  {notifs.map((n) => (
                    <DropdownMenuItem
                      key={n.id}
                      data-testid={`notification-${n.id}`}
                      className="rounded-none border-b border-slate-100 py-3 px-4 cursor-pointer flex flex-col items-start gap-1 last:border-b-0"
                      onClick={() => {
                        if (n.case_id) navigate(isAdmin ? `/admin/cases/${n.case_id}` : `/dashboard/cases/${n.case_id}`);
                        api.post(`/notifications/${n.id}/read`).then(loadNotifs).catch(() => {});
                      }}
                    >
                      <div className="flex w-full items-center justify-between gap-3">
                        <div className={`text-sm font-medium ${n.read ? "text-slate-500" : "text-slate-900"}`}>
                          {n.title}
                        </div>
                        {!n.read && <span className="w-2 h-2 rounded-full bg-emerald-brand shrink-0" />}
                      </div>
                      {n.body && <div className="text-xs text-slate-600 line-clamp-2">{n.body}</div>}
                      <div className="text-[10px] text-slate-400">{formatDate(n.created_at)}</div>
                    </DropdownMenuItem>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="shell-user-trigger"
                  className="ml-1 flex items-center gap-2 h-9 pl-2 pr-3 rounded-full border border-slate-200 bg-white hover:border-navy transition-colors"
                >
                  <div className="w-6 h-6 rounded-full bg-navy text-white grid place-items-center text-[11px] font-semibold">
                    {(user.name || "U").trim().charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden sm:inline text-xs font-semibold text-slate-700 max-w-[120px] truncate">{user.name}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl border border-slate-200 bg-white shadow-xl">
                <DropdownMenuLabel className="text-xs text-slate-500">
                  {user.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="shell-logout"
                  onClick={async () => { await logout(); navigate("/login"); }}
                  className="cursor-pointer text-sm"
                >
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        </div>
      </header>
      <main>{children}</main>
    </div>
  );
}

function NavLink({ to, children, testId, icon }) {
  const location = useLocation();
  const active = location.pathname === to;
  return (
    <Link
      to={to}
      data-testid={testId}
      className={`h-9 px-3 inline-flex items-center gap-2 rounded-full text-xs font-semibold transition-colors ${
        active ? "bg-navy text-white" : "text-slate-700 hover:bg-slate-100 hover:text-navy"
      }`}
    >
      {icon}
      {children}
    </Link>
  );
}
